import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
    IoGrid, IoList, IoSearch, IoSparkles, IoTrendingUp 
} from 'react-icons/io5';
import { RiShoppingBag3Line, RiStarLine, RiFilterLine } from 'react-icons/ri';
import { useProducts, useCategories } from '@/hooks/useProducts';
import Input from '@/components/forms/Input/Input';
import Select from '@/components/forms/Select/Select';
import Button from '@/components/common/Button/Button';
import ProductCard from '@/components/common/ProductCard/ProductCard';
import Spinner from '@/components/common/Spinner/Spinner';
import './Products.css';

interface FilterState {
    search: string;
    category: string;
    materials: string[];
    colors: string[];
    sizes: string[];
    minPrice: string;
    maxPrice: string;
    sortBy: string;
}

const Products: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    
    const [filters, setFilters] = useState<FilterState>({
        search: searchParams.get('search') || '',
        category: searchParams.get('category') || '',
        materials: searchParams.getAll('materials') || [],
        colors: searchParams.getAll('colors') || [],
        sizes: searchParams.getAll('sizes') || [],
        minPrice: searchParams.get('min_price') || '',
        maxPrice: searchParams.get('max_price') || '',
        sortBy: searchParams.get('ordering') || '-created_at',
    });


    // Fetch data
    const { data: productsData, isLoading: productsLoading, error: productsError } = useProducts({
        page: currentPage,
        page_size: 12,
        search: filters.search || undefined,
        category: filters.category ? parseInt(filters.category) : undefined,
        materials: filters.materials.map(m => parseInt(m)),
        colors: filters.colors.map(c => parseInt(c)),
        sizes: filters.sizes.map(s => parseInt(s)),
        min_price: filters.minPrice ? parseFloat(filters.minPrice) : undefined,
        max_price: filters.maxPrice ? parseFloat(filters.maxPrice) : undefined,
        ordering: filters.sortBy,
    });

    const { data: categories = [] } = useCategories();

    // Update URL params when filters change
    useEffect(() => {
        const params = new URLSearchParams();
        
        Object.entries(filters).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(v => v && params.append(key, v));
            } else if (value) {
                params.set(key, value);
            }
        });
        
        if (currentPage > 1) {
            params.set('page', currentPage.toString());
        }
        
        setSearchParams(params);
    }, [filters, currentPage, setSearchParams]);

    const handleFilterChange = (key: keyof FilterState, value: string | string[]) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
    };

    const clearFilters = () => {
        setFilters({
            search: '',
            category: '',
            materials: [],
            colors: [],
            sizes: [],
            minPrice: '',
            maxPrice: '',
            sortBy: '-created_at',
        });
        setCurrentPage(1);
    };


    const sortOptions = [
        { value: '-created_at', label: 'Más Recientes' },
        { value: 'created_at', label: 'Más Antiguos' },
        { value: 'name', label: 'Nombre A-Z' },
        { value: '-name', label: 'Nombre Z-A' },
        { value: 'base_price', label: 'Precio Menor a Mayor' },
        { value: '-base_price', label: 'Precio Mayor a Menor' },
    ];

    const totalPages = productsData ? Math.ceil(productsData.count / 12) : 0;

    if (productsError) {
        return (
            <div className="products_error_container">
                <div className="products_error_content">
                    <h1 className="products_error_title">Error al Cargar Productos</h1>
                    <p className="products_error_message">
                        Ha ocurrido un error al cargar los productos. Por favor, intenta nuevamente.
                    </p>
                    <Button onClick={() => window.location.reload()}>
                        Intentar Nuevamente
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="products_main_container">
            {/* Hero Section */}
            <div className="products_hero_section">
                <div className="products_hero_background"></div>
                <div className="products_hero_overlay"></div>
                <div className="products_hero_content">
                    <div className="products_hero_badge">
                        <IoSparkles size={16} />
                        <span className="products_badge_text">Catálogo Premium</span>
                    </div>
                    <h1 className="products_hero_title">
                        Descubre Nuestros <span className="products_gradient_text">Productos</span> Exclusivos
                    </h1>
                    <p className="products_hero_subtitle">
                        Explora nuestra amplia colección de productos plásticos personalizables de alta calidad. 
                        Desde contenedores hasta soluciones industriales - encuentra exactamente lo que necesitas.
                    </p>
                    <div className="products_hero_stats">
                        <div className="products_stat_item">
                            <RiShoppingBag3Line className="products_stat_icon" />
                            <div className="products_stat_content">
                                <div className="products_stat_number">{productsData?.count || 0}</div>
                                <div className="products_stat_label">Productos Disponibles</div>
                            </div>
                        </div>
                        <div className="products_stat_item">
                            <IoTrendingUp className="products_stat_icon" />
                            <div className="products_stat_content">
                                <div className="products_stat_number">{categories.length || 0}</div>
                                <div className="products_stat_label">Categorías</div>
                            </div>
                        </div>
                        <div className="products_stat_item">
                            <RiStarLine className="products_stat_icon" />
                            <div className="products_stat_content">
                                <div className="products_stat_number">4.9</div>
                                <div className="products_stat_label">Calificación</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Filters and Controls */}
            <div className="products_controls_section">
                <div className="products_controls_container">
                    <div className="products_controls_header">
                        <div className="products_results_info">
                            <h2 className="products_results_title">Todos los Productos</h2>
                            <p className="products_results_count">
                                {productsData?.count ? `${productsData.count} productos encontrados` : 'Cargando productos...'}
                            </p>
                        </div>
                        <div className="products_controls_actions">
                            <Button
                                variant="secondary"
                                onClick={() => setShowFilters(!showFilters)}
                                className="products_filter_toggle"
                            >
                                <RiFilterLine size={16} />
                                Filtros
                            </Button>
                            <div className="products_view_controls">
                                <button
                                    className={`products_view_btn ${viewMode === 'grid' ? 'active' : ''}`}
                                    onClick={() => setViewMode('grid')}
                                >
                                    <IoGrid size={16} />
                                </button>
                                <button
                                    className={`products_view_btn ${viewMode === 'list' ? 'active' : ''}`}
                                    onClick={() => setViewMode('list')}
                                >
                                    <IoList size={16} />
                                </button>
                            </div>
                            <Select
                                value={filters.sortBy}
                                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                                options={sortOptions}
                                placeholder="Ordenar por"
                            />
                        </div>
                    </div>

                    {/* Filters Panel */}
                    {showFilters && (
                        <div className="products_filters_panel">
                            <div className="products_filters_grid">
                                <div className="products_filter_group">
                                    <label className="products_filter_label">Buscar</label>
                                    <Input
                                        name="search"
                                        value={filters.search}
                                        setValue={(value) => handleFilterChange('search', value)}
                                        placeholder="Buscar productos..."
                                        variant="bordered"
                                        startContent={<IoSearch className="products_search_icon" />}
                                    />
                                </div>
                                <div className="products_filter_group">
                                    <label className="products_filter_label">Categoría</label>
                                    <Select
                                        value={filters.category}
                                        onChange={(e) => handleFilterChange('category', e.target.value)}
                                        options={[
                                            { value: '', label: 'Todas las Categorías' },
                                            ...categories.map(cat => ({ value: cat.id.toString(), label: cat.name }))
                                        ]}
                                        placeholder="Seleccionar categoría"
                                    />
                                </div>
                                <div className="products_filter_group">
                                    <label className="products_filter_label">Rango de Precio</label>
                                    <div className="products_price_inputs">
                                        <Input
                                            name="minPrice"
                                            value={filters.minPrice}
                                            setValue={(value) => handleFilterChange('minPrice', value)}
                                            placeholder="Precio mínimo"
                                            variant="bordered"
                                            type="number"
                                        />
                                        <Input
                                            name="maxPrice"
                                            value={filters.maxPrice}
                                            setValue={(value) => handleFilterChange('maxPrice', value)}
                                            placeholder="Precio máximo"
                                            variant="bordered"
                                            type="number"
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="products_filters_actions">
                                <Button variant="secondary" onClick={clearFilters}>
                                    Limpiar Filtros
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Products Grid */}
            <div className="products_grid_section">
                <div className="products_grid_container">
                    {productsLoading ? (
                        <div className="products_loading_container">
                            <Spinner size="lg" />
                            <p className="products_loading_text">Cargando productos...</p>
                        </div>
                    ) : productsData?.results?.length ? (
                        <>
                            <div className={`products_grid ${viewMode === 'list' ? 'products_grid_list' : 'products_grid_cards'}`}>
                                {productsData.results.map((product, index) => (
                                    <ProductCard
                                        key={product.id}
                                        product={product}
                                        variant={viewMode === 'list' ? 'compact' : 'default'}
                                        showDescription={viewMode === 'list'}
                                        animationDelay={index * 0.1}
                                    />
                                ))}
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="products_pagination">
                                    <div className="products_pagination_info">
                                        Mostrando {(currentPage - 1) * 12 + 1} - {Math.min(currentPage * 12, productsData.count)} de {productsData.count} productos
                                    </div>
                                    <div className="products_pagination_controls">
                                        <Button
                                            variant="secondary"
                                            disabled={currentPage === 1}
                                            onClick={() => setCurrentPage(prev => prev - 1)}
                                        >
                                            Anterior
                                        </Button>
                                        <span className="products_pagination_current">
                                            Página {currentPage} de {totalPages}
                                        </span>
                                        <Button
                                            variant="secondary"
                                            disabled={currentPage === totalPages}
                                            onClick={() => setCurrentPage(prev => prev + 1)}
                                        >
                                            Siguiente
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    ) : (
                        <div className="products_empty_state">
                            <div className="products_empty_content">
                                <RiShoppingBag3Line className="products_empty_icon" />
                                <h3 className="products_empty_title">No se encontraron productos</h3>
                                <p className="products_empty_message">
                                    Intenta ajustar tus filtros o buscar con términos diferentes.
                                </p>
                                <Button onClick={clearFilters}>
                                    Limpiar Filtros
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Products;
